import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Upload, Loader2, Trophy, Clock, CheckCircle2, XCircle } from "lucide-react";
import { endpoints, api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function RegisterCar() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ year: "", make: "", model: "", category_id: "", color: "", power_hp: "", mods: "", description: "", photo_url: "", is_ai_generated: false });
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { endpoints.categories().then(setCats).catch(() => {}); }, []);
  if (!user) return <div className="text-center py-20"><p className="text-[#8a8d91] mb-4 font-heading uppercase">Sign in to register a car</p><button onClick={login} className="pas-btn">Sign in</button></div>;

  const genDesc = async () => {
    if (!form.make || !form.model || !form.year) return toast.error("Fill year, make and model first");
    setAiLoading(true);
    try {
      const res = await api.post("/ai/car-description", { year: Number(form.year), make: form.make, model: form.model, mods: form.mods, color: form.color });
      setForm({ ...form, description: res.data.description });
      toast.success("Description generated");
    } catch { toast.error("AI generation failed"); }
    finally { setAiLoading(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, photo_url: `${API}${res.data.url.replace("/api", "")}` });
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.year || !form.make || !form.model || !form.category_id) return toast.error("Year, make, model and category are required");
    setSaving(true);
    try {
      await api.post("/cars", { ...form, year: Number(form.year) });
      toast.success("Car submitted for review");
      navigate("/app/my-entries");
    } catch { toast.error("Could not submit"); }
    finally { setSaving(false); }
  };

  const inp = "w-full bg-[#0f0f11] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#b026ff]";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-car-form">
      <h1 className="font-display text-4xl uppercase text-white">Register a Car</h1>
      <div className="grid grid-cols-3 gap-3">
        <input data-testid="car-year" type="number" placeholder="Year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={inp} />
        <input data-testid="car-make" placeholder="Make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className={`${inp} col-span-2`} />
      </div>
      <input data-testid="car-model" placeholder="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={inp} />
      <select data-testid="car-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className={inp}>
        <option value="">Select a category</option>
        {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="car-color" placeholder="Colour" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className={inp} />
        <input data-testid="car-power" placeholder="Power (hp)" value={form.power_hp} onChange={(e) => setForm({ ...form, power_hp: e.target.value })} className={inp} />
      </div>
      <textarea data-testid="car-mods" placeholder="Mods and build details" rows={3} value={form.mods} onChange={(e) => setForm({ ...form, mods: e.target.value })} className={inp} />

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="font-heading uppercase text-sm text-white">Description</span>
          <button type="button" onClick={genDesc} disabled={aiLoading} className="pas-btn-outline text-xs py-1.5 px-3" data-testid="ai-desc-btn">
            {aiLoading ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} AI generate
          </button>
        </div>
        <textarea data-testid="car-description" placeholder="Tell the crowd about your build" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp} />
      </div>

      <div>
        <label className="border border-dashed border-white/20 bg-[#0f0f11] p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-[#b026ff]" data-testid="car-photo-upload">
          {uploading ? <Loader2 className="animate-spin text-[#ff7a1a]" /> : <Upload className="text-[#ff7a1a]" />}
          <span className="font-heading uppercase text-xs text-[#8a8d91]">{form.photo_url ? "Photo uploaded, tap to change" : "Upload car photo"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
        {form.photo_url && <img src={form.photo_url} alt="" className="mt-3 w-full aspect-[4/3] object-cover border border-white/10" />}
      </div>

      <label className="flex items-center gap-2 text-sm text-[#8a8d91]">
        <input type="checkbox" checked={form.is_ai_generated} onChange={(e) => setForm({ ...form, is_ai_generated: e.target.checked })} className="accent-[#b026ff]" data-testid="car-ai-flag" />
        This photo is AI generated (will be admin verified before public display)
      </label>

      <button disabled={saving} className="pas-btn w-full text-base" data-testid="submit-car-btn">{saving ? "Submitting..." : "Submit for review"}</button>
    </form>
  );
}

const statusMeta = {
  submitted: { icon: Clock, color: "#ffd600", label: "Under review" },
  approved: { icon: CheckCircle2, color: "#3ddc84", label: "Approved" },
  displayed: { icon: CheckCircle2, color: "#3ddc84", label: "On the floor" },
  winner: { icon: Trophy, color: "#ffd600", label: "Winner" },
  rejected: { icon: XCircle, color: "#e8321c", label: "Not accepted" },
  draft: { icon: Clock, color: "#8a8d91", label: "Draft" },
};

export function MyEntries() {
  const { user, login } = useAuth();
  const [cars, setCars] = useState([]);
  useEffect(() => { if (user) api.get("/my-cars").then((r) => setCars(r.data)).catch(() => {}); }, [user]);
  if (!user) return <div className="text-center py-20"><button onClick={login} className="pas-btn">Sign in</button></div>;
  return (
    <div className="space-y-4">
      <h1 className="font-display text-4xl uppercase text-white">My Entries</h1>
      {cars.length === 0 && <p className="text-[#8a8d91] font-body">No entries yet. Register your first car!</p>}
      {cars.map((c) => {
        const m = statusMeta[c.status] || statusMeta.draft;
        return (
          <div key={c.id} className="flex gap-3 border border-white/10 bg-[#121212] overflow-hidden" data-testid={`entry-${c.id}`}>
            {c.photo_url && <img src={c.photo_url} alt="" className="w-24 h-24 object-cover" />}
            <div className="py-3 flex-1">
              <div className="font-heading text-white uppercase">{c.year} {c.make} {c.model}</div>
              <div className="text-xs text-[#8a8d91]">{c.category_name} · {c.vote_count} votes</div>
              <div className="mt-1 inline-flex items-center gap-1 text-xs font-heading uppercase" style={{ color: m.color }}>
                <m.icon size={13} /> {m.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Favourites() {
  const { user, login } = useAuth();
  const [cars, setCars] = useState([]);
  useEffect(() => { if (user) api.get("/favourites").then((r) => setCars(r.data)).catch(() => {}); }, [user]);
  if (!user) return <div className="text-center py-20"><button onClick={login} className="pas-btn">Sign in</button></div>;
  return (
    <div className="space-y-4">
      <h1 className="font-display text-4xl uppercase text-white">Favourites</h1>
      {cars.length === 0 && <p className="text-[#8a8d91] font-body">No favourites yet. Tap the star on any car to save it.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {cars.map((c) => (
          <a key={c.id} href={`/app/cars/${c.id}`} className="border border-white/10 bg-[#121212] overflow-hidden card-hover">
            <img src={c.photo_url} alt="" className="w-full aspect-[4/3] object-cover" />
            <div className="p-3">
              <div className="text-[#ff7a1a] font-heading text-xs uppercase">{c.year}</div>
              <div className="font-heading text-white uppercase">{c.make} {c.model}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
